import {
	BI_REPORTS_CONFIG,
	BI_REPORTS_MESSAGES,
} from 'components/reports/biReportEmbed/utils/biReports';
import {
	getReportToken,
	getUserToken,
} from 'services/api/requests/biIntegration';
import {
	biExplorer,
	BiReportType,
} from 'services/api/variables/external/biExplorer';

import {
	IBiEmbedParams,
	IBiEmbedWindow,
	IBiScriptInfo,
	TBiError,
} from '../biReportEmbed.types';
import { biTokenCache } from './biTokenCache';

const scriptCache = new Map<string, IBiScriptInfo>();

export const getBiUserToken = async (): Promise<string> => {
	const cached = biTokenCache.get('USER_TOKEN');
	if (cached) return cached;

	const token = await getUserToken();
	if (!token) {
		throw new Error(BI_REPORTS_MESSAGES.MSG_BI_TOKEN_NOT_FOUND);
	}

	biTokenCache.set('USER_TOKEN', token);
	return token;
};

export const getBiReportToken = async (
	value: BiReportType
): Promise<string> => {
	const key = `REPORT_TOKEN:${value}`;
	const cached = biTokenCache.get(key);
	if (cached) {
		return cached;
	}

	const token = await getReportToken(value);
	if (!token) {
		throw new Error(BI_REPORTS_MESSAGES.MSG_BI_TOKEN_NOT_FOUND);
	}

	biTokenCache.set(key, token);
	return token;
};

const loadBiScript = (domain: string): Promise<void> => {
	return new Promise((resolve, reject) => {
		const scriptUrl = `${domain}${BI_REPORTS_CONFIG.SCRIPT_PATH}`;

		const cachedScript = scriptCache.get(scriptUrl);
		if (cachedScript?.isLoaded) {
			resolve();
			return;
		}

		const script = document.createElement('script');
		script.async = true;
		script.src = scriptUrl;

		script.onload = () => {
			scriptCache.set(scriptUrl, {
				url: scriptUrl,
				isLoaded: true,
				element: script,
			});
			resolve();
		};

		script.onerror = () => {
			scriptCache.delete(scriptUrl);
			reject(new Error(BI_REPORTS_MESSAGES.MSG_BI_SCRIPT_LOAD_ERROR));
		};

		const timeout = setTimeout(() => {
			reject(new Error(BI_REPORTS_MESSAGES.MSG_BI_SCRIPT_LOAD_ERROR));
		}, BI_REPORTS_CONFIG.SCRIPT_LOAD_TIMEOUT);

		script.onload = () => {
			clearTimeout(timeout);
			scriptCache.set(scriptUrl, {
				url: scriptUrl,
				isLoaded: true,
				element: script,
			});
			resolve();
		};

		scriptCache.set(scriptUrl, {
			url: scriptUrl,
			isLoaded: false,
			element: script,
		});

		const firstScript = document.getElementsByTagName('script')[0];
		firstScript.parentNode?.insertBefore(script, firstScript);
	});
};

const validateContainer = (containerId: string): HTMLElement => {
	const container = document.getElementById(containerId);
	if (!container) {
		throw new Error(BI_REPORTS_MESSAGES.MSG_BI_CONTAINER_ERROR);
	}
	return container;
};

const validateBiEmbedFunction = (window: IBiEmbedWindow): boolean => {
	return typeof window.biEmbedded === 'function';
};

export const openSpecificReport = async (
	reportToken: string,
	containerId: string
): Promise<void> => {
	const domain =
		biExplorer[process.env.REACT_APP_FRONTEND_NODE_ENV || 'development']
			.url;

	if (!domain) {
		throw new Error(BI_REPORTS_MESSAGES.MSG_BI_TOKEN_NOT_CONFIGURED);
	}

	try {
		const userToken = await getBiUserToken();
		const container = validateContainer(containerId);

		container.innerHTML = '';

		await loadBiScript(domain);

		const windowWithBi = window as IBiEmbedWindow;
		if (!validateBiEmbedFunction(windowWithBi)) {
			throw new Error(BI_REPORTS_MESSAGES.MSG_BI_SCRIPT_LOAD_ERROR);
		}

		const embedParams: IBiEmbedParams = {
			p: `/${reportToken}/reports/view`,
			m: false, //Se é modal ou não
		};

		windowWithBi.biEmbedded!(
			windowWithBi,
			domain,
			`#${containerId}`,
			userToken,
			embedParams
		);
	} catch (error) {
		const container = document.getElementById(containerId);
		if (container) {
			container.innerHTML = `
                <div style="padding: 20px; text-align: center; color: red.500;">
                    ${
						error instanceof Error
							? error.message
							: BI_REPORTS_MESSAGES.MSG_BI_AUTH_ERROR
					}
                </div>
            `;
		}
		throw error;
	}
};

export const openSalesDashboard = async (
	containerId: string
): Promise<void> => {
	const token = await getBiReportToken(BiReportType.SALES_REPORT);
	await openSpecificReport(token, containerId);
};

export const openDemandDashboard = async (
	containerId: string
): Promise<void> => {
	const token = await getBiReportToken(BiReportType.DEMAND_REPORT);
	await openSpecificReport(token, containerId);
};

/**
 * Abre o BI Explorer completo (portal) sem relatório específico
 * @param containerId ID do container onde o BI será embedado
 */
export const openBiExplorerPortal = async (
	containerId: string
): Promise<void> => {
	const domain =
		biExplorer[process.env.REACT_APP_FRONTEND_NODE_ENV || 'development']
			.url;

	if (!domain) {
		throw new Error(BI_REPORTS_MESSAGES.MSG_BI_TOKEN_NOT_CONFIGURED);
	}

	try {
		const userToken = await getBiUserToken();
		const container = validateContainer(containerId);

		container.innerHTML = '';

		await loadBiScript(domain);

		const windowWithBi = window as IBiEmbedWindow;
		if (!validateBiEmbedFunction(windowWithBi)) {
			throw new Error(BI_REPORTS_MESSAGES.MSG_BI_SCRIPT_LOAD_ERROR);
		}

		// Para o portal completo, usamos path '/' sem especificar relatório
		const embedParams: IBiEmbedParams = {
			p: '/',
			m: false, // Não é modal
		};

		windowWithBi.biEmbedded!(
			windowWithBi,
			domain,
			`#${containerId}`,
			userToken,
			embedParams
		);
	} catch (error) {
		const container = document.getElementById(containerId);
		if (container) {
			container.innerHTML = `
                <div style="padding: 20px; text-align: center; color: red.500;">
                    ${
						error instanceof Error
							? error.message
							: BI_REPORTS_MESSAGES.MSG_BI_AUTH_ERROR
					}
                </div>
            `;
		}
		throw error;
	}
};

export const cleanupBiEmbed = (containerId?: string): void => {
	if (containerId) {
		const container = document.getElementById(containerId);
		if (container) {
			container.innerHTML = '';
		}
	}
};

export const resizeBiEmbed = (containerId: string): void => {
	try {
		const container = document.getElementById(containerId);
		if (!container) {
			return;
		}

		const windowWithBi = window as IBiEmbedWindow;

		const containerElement = container.parentElement;
		if (containerElement) {
			containerElement.style.width = '100%';
			containerElement.style.height = '100%';
			containerElement.style.padding = '0';
			containerElement.style.margin = '0';
			containerElement.style.overflow = 'hidden';
		}

		container.style.width = '100%';
		container.style.height = '100%';
		container.style.padding = '0';
		container.style.margin = '0';
		container.style.overflow = 'hidden';
		container.style.boxSizing = 'border-box';

		if (typeof windowWithBi.biEmbedded === 'function') {
			const iframe = container.querySelector('iframe');
			if (iframe) {
				const containerRect = container.getBoundingClientRect();
				const containerWidth =
					containerRect.width || container.offsetWidth;
				const containerHeight =
					containerRect.height || container.offsetHeight;

				iframe.style.width = `${containerWidth}px`;
				iframe.style.height = `${containerHeight}px`;
				iframe.style.minWidth = '100%';
				iframe.style.minHeight = '100%';
				iframe.style.maxWidth = '100%';
				iframe.style.maxHeight = '100%';
				iframe.style.border = 'none';
				iframe.style.display = 'block';
				iframe.style.boxSizing = 'border-box';

				const allChildren = container.querySelectorAll('*');
				for (const child of Array.from(allChildren)) {
					const element = child as HTMLElement;
					if (element !== container && element !== iframe) {
						element.style.boxSizing = 'border-box';
						if (element.tagName === 'DIV') {
							element.style.width = '100%';
							element.style.height = '100%';
						}
					}
				}

				if ((windowWithBi as any).powerbi?.resize) {
					(windowWithBi as any).powerbi.resize(container);
				}
			}
		}
	} catch (error) {
		// eslint-disable-next-line no-console
		console.warn('Erro ao redimensionar BI embed:', error);
	}
};

export const getBiErrorType = (error: Error | string): TBiError => {
	const message = typeof error === 'string' ? error : error.message;

	if (message.includes('token')) return 'TOKEN_NOT_FOUND';
	if (message.includes('container')) return 'CONTAINER_NOT_FOUND';
	if (message.includes('script')) return 'SCRIPT_LOAD_FAILED';
	if (message.includes('function')) return 'EMBED_FUNCTION_NOT_FOUND';
	if (message.includes('configuration')) return 'CONFIGURATION_MISSING';
	if (message.includes('user')) return 'USER_INFO_NOT_FOUND';
	if (message.includes('auth')) return 'AUTHENTICATION_FAILED';

	return 'UNKNOWN_ERROR';
};
